import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import CustomisableRibbon from 'src/components/customisable-ribbon/CustomisableRibbon';
import ExportButton from 'src/components/export-button';
import TabHeader from 'src/components/tab-header';
import type { useGetAttestationsRegister } from 'src/hooks/queries/attestations/useGetAttestationsRegister';
import { useRibbonAndExport } from 'src/hooks/useRibbonAndExport';
import { PageLayout } from 'src/layouts';
import { getCounter } from 'src/utils';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { AttestationRegisterToolbar } from '../AttestationRegisterToolbar';
import { useGetRegisterTableProps } from './config';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

type GetPolicyAttestationRecordsData = NonNullable<
  ReturnType<typeof useGetAttestationsRegister>['data']
>;

export interface AllViewProps {
  attestationsRegisterData: GetPolicyAttestationRecordsData | undefined;
  loading: boolean;
  viewSelector: React.ReactNode;
  title: string;
  subTitle?: string;
}

const View: React.FC<AllViewProps> = ({
  attestationsRegisterData,
  loading,
  viewSelector,
  title,
  subTitle,
}) => {
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const tableProps = useGetRegisterTableProps(
    attestationsRegisterData?.attestation_record
  );
  const counter = getCounter(tableProps.totalItemsCount, loading);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  const header = (
    <div className={'flex justify-between'}>
      <TabHeader>{subTitle}</TabHeader>
      {viewSelector}
    </div>
  );

  return (
    <PageLayout
      helpTranslationKey={'policy.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-176) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <AttestationRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
          />
        ) : undefined
      }
      actions={
        <ExportButton
          tableProps={tableProps}
          entityLabel={title}
          {...ribbonExportProps}
        />
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.AttestationRecord}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} header={header} />
    </PageLayout>
  );
};

export default View;
